import { cn } from "@/utils";
import type { LucideIcon } from "lucide-react";
import React, { useState } from "react";
import { RailChevron, RailDisclosureIcon } from "./RailChevron";
import { RailRowOpenButton } from "./RailRowOpenButton";
import { useSingleOrDoubleClick } from "./useSingleOrDoubleClick";

/** Generic expandable row (folders, connectors, etc.). Whole row toggles open state. */
export function RailExpandableRow({
  icon,
  openIcon,
  label,
  defaultOpen = false,
  dragRef,
  dragProps,
  isDragging,
  children,
}: {
  icon: LucideIcon;
  openIcon?: LucideIcon;
  label: string;
  defaultOpen?: boolean;
  /**
   * Optional dnd-kit drag handle. When set, the header doubles as a drag
   * source (toggle still fires on click — the PointerSensor's 8px
   * activation distance keeps the two from colliding).
   */
  dragRef?: (element: HTMLElement | null) => void;
  dragProps?: React.HTMLAttributes<HTMLElement>;
  isDragging?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="flex flex-col">
      <button
        type="button"
        ref={dragRef}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        data-state={open ? "open" : "closed"}
        className={cn(
          "flex min-h-7 w-full items-center gap-2.5 rounded-md px-2 text-left hover:bg-n-05",
          isDragging && "opacity-60",
        )}
        {...dragProps}
      >
        <RailDisclosureIcon
          open={open}
          icon={open && openIcon ? openIcon : icon}
        />
        <span className="flex-1 truncate text-base text-n-80">{label}</span>
      </button>
      {open && <div className="flex flex-col pl-2">{children}</div>}
    </div>
  );
}

/** Workspace row. One gesture set across the rail: single click on the
 *  icon+title expands/collapses the children in place; double click opens the
 *  workspace detail page (`onOpen`). The always-visible chevron button is an
 *  instant toggle from anywhere. */
export function RailWorkspaceExpandableRow({
  icon: TitleIcon,
  iconNode,
  label,
  active,
  trailing,
  menu,
  open: controlledOpen,
  onOpenChange,
  onOpen,
  defaultOpen = false,
  onContextMenu,
  children,
}: {
  icon: LucideIcon;
  /** Optional custom icon (e.g. the workspace's chosen colored icon).
   *  When set it replaces `icon` next to the title. */
  iconNode?: React.ReactNode;
  label: string;
  active?: boolean;
  trailing?: React.ReactNode;
  menu?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Navigate to the workspace (title double-click; also in the kebab). */
  onOpen?: () => void;
  defaultOpen?: boolean;
  /** Right-click handler — opens the row's `menu` (when it has one). */
  onContextMenu?: (e: React.MouseEvent) => void;
  children: React.ReactNode;
}) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const open = controlledOpen ?? uncontrolledOpen;

  const setOpen = (next: boolean) => {
    if (onOpenChange) onOpenChange(next);
    else setUncontrolledOpen(next);
  };
  const toggleOpen = () => setOpen(!open);

  // Exclusive gestures on the title: a double-click must open the workspace
  // without the single-click toggle firing (twice) around it. The chevron
  // button stays an instant toggle — it has no double action to collide with.
  const titleClicks = useSingleOrDoubleClick(toggleOpen, () => onOpen?.(), {
    delayed: !!onOpen,
  });

  return (
    <div className="flex flex-col has-[[data-rail-canvas-row]:hover]:[&>[data-workspace-row]]:!bg-transparent">
      <div
        data-workspace-row
        data-state={open ? "open" : "closed"}
        onContextMenu={onContextMenu}
        className={cn(
          "group/row relative flex min-h-7 w-full items-center gap-2.5 rounded-md px-2 hover:bg-n-05",
          menu && "pr-8",
          active && "bg-n-05",
        )}
      >
        <button
          type="button"
          onClick={toggleOpen}
          aria-expanded={open}
          aria-label={open ? `Collapse ${label}` : `Expand ${label}`}
          title={open ? "Collapse" : "Expand"}
          className="-m-1 inline-flex shrink-0 rounded-sm p-1 hover:bg-n-10"
        >
          <RailChevron open={open} />
        </button>
        <button
          type="button"
          onClick={titleClicks.onClick}
          onDoubleClick={titleClicks.onDoubleClick}
          aria-expanded={open}
          /**
           * Don't take focus on mousedown. The default would blur whatever the
           * user was in — typically the composer, which autofocuses — so the
           * composer's focus ring would drop on mousedown and spring back on
           * mouseup once navigation remounted and refocused it. That round trip
           * is the flash when opening a workspace from the rail. Suppressing
           * the default keeps focus where it is until the surface actually
           * swaps; `click` still fires on mouseup, and keyboard focus is
           * untouched (Tab and Enter never go through mousedown).
           */
          onMouseDown={(e) => e.preventDefault()}
          // -ml-1 tightens the chevron→icon gap to match RailDisclosureIcon's
          // gap-1.5 (the row's gap-2.5 minus 4px).
          className="-ml-1 flex min-w-0 flex-1 select-none items-center self-stretch gap-2.5 text-left"
        >
          {iconNode ? (
            <span className="flex size-4 shrink-0 items-center justify-center">
              {iconNode}
            </span>
          ) : (
            <TitleIcon className="size-4 shrink-0 text-n-50" />
          )}
          <span className="min-w-0 flex-1 truncate text-base text-n-80">
            {label}
          </span>
        </button>
        {/* Mirrors the title double-click: open the workspace directly. */}
        {onOpen && <RailRowOpenButton label={label} onOpen={onOpen} />}
        {/* `trailing` lives OUTSIDE the navigation button — it carries
            interactive controls (Upload action, members/share indicator), and
            a <button> can't nest inside the title <button>. */}
        {trailing}
        {menu}
      </div>
      {open && <div className="flex flex-col pl-2">{children}</div>}
    </div>
  );
}
